import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Tv,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Clock,
  QrCode,
  Sparkles,
  Building2,
  Sun,
  CloudSun,
} from 'lucide-react';
import type { ComunicadosConfig, ComunicadoItem } from './ComunicadosConfig';

interface Props {
  config: {
    branding: {
      tenantName: string;
      tenantBadge?: string;
      logoUrl?: string;
      primaryColor?: string;
      secondaryColor?: string;
      slogan?: string;
      autoridadeNome?: string;
      autoridadeCargo?: string;
    };
    scope?: string;
    scopeId?: string;
  };
  comunicados: ComunicadoItem[];
  midias?: Array<{ id: string; titulo: string; arquivoUrl: string; categoria: string }>;
  isLoading?: boolean;
}

export function TvSignagePlayerCore({ config, comunicados, midias = [], isLoading }: Props) {
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [time, setTime] = useState(new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  // Slides a serem exibidos (folders do banco de mídias + comunicados ativos com imagem ou texto)
  const slides = useMemo(() => {
    const list: Array<{
      id: string;
      titulo: string;
      descricao?: string;
      imageUrl?: string;
      tipo: string;
      categoria: string;
    }> = [];

    // Adiciona mídias do acervo
    midias.forEach((m) => {
      list.push({
        id: `mid-${m.id}`,
        titulo: m.titulo,
        imageUrl: m.arquivoUrl,
        tipo: 'FOLDER OFICIAL',
        categoria: m.categoria,
      });
    });

    // Adiciona comunicados ativos
    comunicados.forEach((c) => {
      if (c.ativo !== false) {
        list.push({
          id: `com-${c.id}`,
          titulo: c.titulo,
          descricao: c.corpo,
          imageUrl: c.configPreview?.midiaUrl,
          tipo: c.tipo || 'COMUNICADO',
          categoria: c.categoria || 'OPERACIONAL',
        });
      }
    });

    return list.length > 0
      ? list
      : [
          {
            id: 'placeholder',
            titulo: `Bem-vindo a ${config.branding.tenantName}`,
            descricao: 'Painel Digital Informativo — Sistema Aluno Conectado.',
            tipo: 'INFORMATIVO',
            categoria: 'INSTITUCIONAL',
          },
        ];
  }, [midias, comunicados, config.branding.tenantName]);

  // Relógio em tempo real
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Transição automática de slides (12 segundos por slide)
  useEffect(() => {
    if (!isPlaying || slides.length <= 1) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 12000);
    return () => clearInterval(interval);
  }, [isPlaying, slides.length]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const currentSlide = slides[index] || slides[0];

  const timeStr = time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = time.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: isFullscreen ? '100vh' : 'calc(100vh - 70px)',
        background: '#090d16',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Barra de Topo da TV: Identidade + Clima + Relógio */}
      <div
        style={{
          padding: '16px 28px',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {config.branding.logoUrl ? (
            <img
              src={config.branding.logoUrl}
              alt="Logo"
              style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', background: 'white', padding: 2 }}
            />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 size={24} />
            </div>
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, letterSpacing: '-0.02em' }}>
              {config.branding.tenantName}
            </h2>
            <span style={{ fontSize: '.76rem', color: '#94a3b8' }}>
              {config.branding.tenantBadge || 'Painel de Transmissão Digital'}
            </span>
          </div>
        </div>

        {/* Relógio e Clima */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#fbbf24', fontSize: '.88rem', fontWeight: 700 }}>
            <Sun size={20} />
            <span>24°C • Ensolarado</span>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#38bdf8', letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' }}>
              {timeStr}
            </div>
            <div style={{ fontSize: '.72rem', color: '#94a3b8', textTransform: 'capitalize' }}>
              {dateStr}
            </div>
          </div>

          <button
            type="button"
            onClick={toggleFullscreen}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: 'white',
              padding: '8px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '.78rem',
              fontWeight: 700,
            }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            {isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
          </button>
        </div>
      </div>

      {/* Conteúdo Principal do Slide (Centro) */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 40px',
          position: 'relative',
          zIndex: 5,
        }}
      >
        {currentSlide.imageUrl ? (
          <div style={{
            width: '100%',
            height: '100%',
            maxHeight: '74vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
            background: '#000',
          }}>
            <img
              src={currentSlide.imageUrl}
              alt={currentSlide.titulo}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        ) : (
          <div style={{
            maxWidth: 880,
            width: '100%',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid #334155',
            borderRadius: 20,
            padding: '36px 44px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
            textAlign: 'center',
          }}>
            <span style={{
              display: 'inline-block',
              padding: '4px 14px',
              borderRadius: 20,
              background: '#2563eb30',
              color: '#60a5fa',
              fontSize: '.82rem',
              fontWeight: 800,
              marginBottom: 16,
              textTransform: 'uppercase',
            }}>
              {currentSlide.tipo}
            </span>

            <h1 style={{ fontSize: '2.2rem', fontWeight: 900, margin: '0 0 16px', lineHeight: 1.25, color: '#f8fafc' }}>
              {currentSlide.titulo}
            </h1>

            {currentSlide.descricao && (
              <p style={{ fontSize: '1.15rem', color: '#cbd5e1', lineHeight: 1.6, margin: '0 auto', maxWidth: 720, whiteSpace: 'pre-wrap' }}>
                {currentSlide.descricao}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Barra de Rodapé da TV: QR Code + Controles + Barra de Progresso */}
      <div
        style={{
          padding: '14px 28px',
          background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 10,
        }}
      >
        {/* QR Code de Acesso para os Pais */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.08)', padding: '6px 14px', borderRadius: 10 }}>
          <div style={{ background: 'white', padding: 4, borderRadius: 6, display: 'flex' }}>
            <QrCode size={32} color="#000" />
          </div>
          <div>
            <div style={{ fontSize: '.78rem', fontWeight: 800, color: '#f8fafc' }}>Acesse pelo Celular</div>
            <div style={{ fontSize: '.68rem', color: '#94a3b8' }}>Aponte a câmera e abra o app Aluno Conectado</div>
          </div>
        </div>

        {/* Controles de Slides */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => setIndex((prev) => (prev - 1 + slides.length) % slides.length)}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: 8, borderRadius: '50%', cursor: 'pointer' }}
          >
            <ChevronLeft size={16} />
          </button>

          <button
            type="button"
            onClick={() => setIsPlaying(!isPlaying)}
            style={{ background: '#2563eb', border: 'none', color: 'white', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', fontWeight: 700 }}
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
            {isPlaying ? 'Pausar Transmissão' : 'Iniciar Loop'}
          </button>

          <button
            type="button"
            onClick={() => setIndex((prev) => (prev + 1) % slides.length)}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: 8, borderRadius: '50%', cursor: 'pointer' }}
          >
            <ChevronRight size={16} />
          </button>

          <span style={{ fontSize: '.74rem', color: '#94a3b8', marginLeft: 8 }}>
            Slide {index + 1} de {slides.length}
          </span>
        </div>
      </div>
    </div>
  );
}
